import { Fragment, type ReactNode } from 'react';

import { createMqStyles } from '@fhs/ui';

import { Layout } from './layout/layout';
import type { RendererProps } from './types';
import { addActionSource } from './utils/action';
import { WidgetAlert } from './widgets/alert';
import { WidgetHeroVideo } from './widgets/hero-video';
import { WidgetMarketingTileRow } from './widgets/marketing-tile-row';

function Widgets(props: Pick<RendererProps, 'widgets' | 'onAction' | 'legacyHomeCard'>) {
  const mqStyles = useMqStyles();

  return props.widgets.map((widget, index): ReactNode => {
    const isFirst = index === 0;

    switch (widget._type) {
      case 'pageBuilderWidgetHeroVideo': {
        return (
          <WidgetHeroVideo
            key={widget._id}
            onAction={addActionSource(
              {
                widgetId: widget.internalName ?? widget._id,
                widgetType: 'hero-video',
              },
              props.onAction
            )}
            video={widget.video}
            prefixType={widget.prefixType}
            prefixImage={widget.prefixImage}
            prefixText={widget.prefixText}
            title={widget.title}
            description={widget.description}
            buttons={widget.buttons}
          />
        );
      }

      case 'pageBuilderWidgetMarketingTileRow': {
        return (
          <WidgetMarketingTileRow
            key={widget._id}
            onAction={addActionSource(
              {
                widgetId: widget.internalName ?? widget._id,
                widgetType: 'marketing-tile-row',
              },
              props.onAction
            )}
            mediaLayoutDesktop={widget.mediaLayoutDesktop}
            mediaType={widget.mediaType}
            image={widget.image}
            prefixType={widget.prefixType}
            prefixImage={widget.prefixImage}
            prefixText={widget.prefixText}
            title={widget.title}
            description={widget.description}
            buttons={widget.buttons}
          />
        );
      }

      case 'pageBuilderWidgetAlert': {
        return (
          <WidgetAlert
            key={widget._id}
            onAction={addActionSource(
              {
                widgetId: widget.internalName ?? widget._id,
                widgetType: 'alert',
              },
              props.onAction
            )}
            colorVariant={widget.colorVariant}
            alignment={widget.alignment}
            leadingIcon={widget.leadingIcon}
            content={widget.content}
            trailingActions={widget.trailingActions}
            style={[isFirst && mqStyles.removeBottomGap]}
          />
        );
      }

      case 'pageBuilderWidgetLegacyHomeCard': {
        return <Fragment key={widget._id}>{props.legacyHomeCard ?? null}</Fragment>;
      }
    }
  });
}

const useMqStyles = createMqStyles({
  removeBottomGap: {
    $base: {
      marginBottom: -24,
    },
    $gteDesktop: {
      marginBottom: -32,
    },
  },
});

export function PageBuilderRenderer(props: RendererProps) {
  return (
    <Layout layout={props.layout} onAction={props.onAction}>
      <Widgets
        widgets={props.widgets}
        onAction={props.onAction}
        legacyHomeCard={props.legacyHomeCard}
      />
    </Layout>
  );
}
